import { useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { api, tokens, type User } from "./api";
import { BatchProvider } from "./batch";
import { BottomNav, TopBar } from "./components";
import { Bipar } from "./screens/Bipar";
import { Login } from "./screens/Login";
import { MercadoLivre } from "./screens/MercadoLivre";
import { Retirada } from "./screens/Retirada";
import { Devolucao, Historico, Perfil } from "./screens/Outras";

/// A ordem das abas, da esquerda para a direita — a mesma do menu de baixo.
/// É por ela que o dedo anda: arrastar para a esquerda avança nesta lista,
/// para a direita volta.
const ABAS = ["/", "/historico", "/devolucao", "/retirada", "/mercado-livre", "/perfil"];

/// As abas do Setor 2 — só o que ele de fato alcança.
const ABAS_SETOR2 = ["/devolucao", "/perfil"];

/// Quanto o dedo precisa andar, em pixels, para contar como troca de aba.
///
/// Curto demais e um toque tremido na hora de bipar vira navegação
/// acidental — já é a queixa mais comum de gesto em celular. 70px é mais que
/// o dedo se move sem querer, e menos que um arrasto de verdade custa.
const DISTANCIA_MINIMA = 70;

/// O gesto lateral que troca de aba, tipo carrossel de fotos.
///
/// Fica no nível do App, e não em cada tela, porque a lista de abas é uma
/// coisa só, compartilhada — repetir a lógica em Bipar, Histórico, Retirada
/// etc. arriscaria cada uma decidir "próxima aba" de um jeito diferente.
///
/// Ignora o arrasto por cima da câmera ou de qualquer janela em primeiro
/// plano (`.scan-screen`, `.sobreposicao`): ali o gesto lateral já significa
/// outra coisa, ou não deveria significar nada — trocar de aba com a câmera
/// aberta tiraria o operador do meio de uma leitura sem ele pedir.
function useTrocarDeAbaComGesto(abas: string[]) {
  const navigate = useNavigate();
  const location = useLocation();
  const inicio = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const aoComecar = (e: TouchEvent) => {
      const alvo = e.target as HTMLElement;
      if (alvo.closest(".scan-screen, .sobreposicao")) {
        inicio.current = null;
        return;
      }
      const t = e.touches[0];
      inicio.current = t ? { x: t.clientX, y: t.clientY } : null;
    };

    const aoSoltar = (e: TouchEvent) => {
      const partida = inicio.current;
      inicio.current = null;
      if (!partida) return;

      const t = e.changedTouches[0];
      if (!t) return;

      const dx = t.clientX - partida.x;
      const dy = t.clientY - partida.y;

      // Predominantemente horizontal: um arrasto na diagonal, tentando
      // rolar a lista para baixo, não pode virar troca de aba por acidente.
      if (Math.abs(dx) < DISTANCIA_MINIMA || Math.abs(dx) < Math.abs(dy) * 1.5) return;

      const atual = abas.indexOf(location.pathname);
      if (atual === -1) return;

      // Arrastar para a esquerda avança; para a direita, volta — como
      // folhear páginas ou trocar de foto.
      const proxima = atual + (dx < 0 ? 1 : -1);
      if (proxima < 0 || proxima >= abas.length) return;

      navigate(abas[proxima]!);
    };

    document.addEventListener("touchstart", aoComecar, { passive: true });
    document.addEventListener("touchend", aoSoltar, { passive: true });
    return () => {
      document.removeEventListener("touchstart", aoComecar);
      document.removeEventListener("touchend", aoSoltar);
    };
  }, [location.pathname, navigate, abas]);
}

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [checking, setChecking] = useState(true);
  const [count, setCount] = useState(0);

  // Retoma a sessão sem novo login quando o token guardado ainda vale — o
  // operador abre o app dezenas de vezes por dia.
  useEffect(() => {
    if (!tokens.access) {
      setChecking(false);
      return;
    }

    void api
      .me()
      .then(setUser)
      .catch(() => tokens.clear())
      .finally(() => setChecking(false));
  }, []);

  // O número ao lado do nome é O QUE ESTA PESSOA bipou na coleta aberta.
  //
  // Era o total da SESSÃO, que abria no primeiro bipe e só fechava se alguém
  // apertasse um botão que ninguém apertava — na prática somava desde sempre,
  // e o número no alto da tela não queria dizer nada.
  //
  // Reconfere sozinho porque a coleta pode ser fechada por outra pessoa, do
  // computador ou de outro celular: sem isso, o cabeçalho continuaria exibindo
  // o número de uma coleta que já saiu pela porta.
  useEffect(() => {
    if (!user) return;

    const ler = () => void api.minhaColeta().then((c) => setCount(c.meuTotal)).catch(() => undefined);

    ler();
    const t = setInterval(ler, 20_000);
    return () => clearInterval(t);
  }, [user]);

  /// O Setor 2 só cuida do cliente depois que o pedido já foi separado —
  /// devolução é o único trabalho dele aqui. Bipar despacho, ver histórico de
  /// saída, confirmar retirada e o fluxo do Mercado Livre são tarefas de quem
  /// separa, não dele.
  const naSetor2 = user?.role === "SETOR2";

  useTrocarDeAbaComGesto(naSetor2 ? ABAS_SETOR2 : ABAS);

  if (checking) return null;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <BatchProvider>
    <div className="app">
      <TopBar name={user.name} count={count} />

      <Routes>
        {naSetor2 ? (
          <>
            <Route path="/devolucao" element={<Devolucao />} />
            <Route
              path="/perfil"
              element={<Perfil user={user} onLogout={() => setUser(null)} />}
            />
            {/* Qualquer outra rota (inclusive a raiz, que seria Bipar) volta
                pra Devolução — sem menu pra chegar lá, mas um link salvo ou
                digitado direto não pode abrir uma tela que não é dele. */}
            <Route path="*" element={<Navigate to="/devolucao" replace />} />
          </>
        ) : (
          <>
            <Route path="/" element={<Bipar />} />
            <Route path="/historico" element={<Historico />} />
            <Route path="/devolucao" element={<Devolucao />} />
            <Route path="/mercado-livre" element={<MercadoLivre />} />
            <Route path="/retirada" element={<Retirada />} />
            <Route
              path="/perfil"
              element={<Perfil user={user} onLogout={() => setUser(null)} />}
            />
          </>
        )}
      </Routes>

      <BottomNav somenteDevolucao={naSetor2} />
    </div>
    </BatchProvider>
  );
}
